import _get from 'lodash/get'
import _noop from 'lodash/noop'
import Dialog from '@mui/material/Dialog'
import { styled } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import PersonIcon from '@mui/icons-material/Person'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Fab, Grid, Tooltip, useTheme } from '@mui/material'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import { createContext, lazy, Suspense, ReactElement, useContext, useMemo, useState, Fragment } from 'react'

import Spinner from '../layouts/Spinner'
import { FormDialogType } from '../../enums/enums'
import useGetExtensionMessage from '../../hooks/useGetExtensionMessage'
import { ExtensionOrganizationData, ExtensionProspectData } from '../../types'

const TaskForm = lazy(() => import('../../views/tasks/TaskFormDialog'))
const DealForm = lazy(() => import('../../views/deals/DealFormDialog'))
const ProspectForm = lazy(() => import('../../views/prospects/ProspectFormDialog'))
const OrganizationForm = lazy(() => import('../../views/organizations/OrganizationFormDialog'))

const FormType = {
  [FormDialogType.prospect]: ProspectForm,
  [FormDialogType.organization]: OrganizationForm,
  [FormDialogType.deal]: DealForm,
  [FormDialogType.task]: TaskForm,
}

const PREFIX = 'shortCuts'

const classes = {
  group: `${PREFIX}-group`,
  shortCutBtn: `${PREFIX}-shortCutBtn`,
}

const StyledGrid = styled(Grid)(() => ({
  [`& .${classes.group}`]: {
    display: 'none',
    zIndex: -1,
  },

  '&:hover': {
    [`& .${classes.group}`]: {
      display: 'block',
      zIndex: 'unset',
    },
  },
}))

interface State {
  open: boolean
  type?: FormDialogType
  subType?: FormDialogType
  openSubDialog: boolean
  options?: { extensionData?: ExtensionProspectData | ExtensionOrganizationData; editMode?: boolean; id?: number }
}

interface FormContextProps {
  openDialog: (
    type: FormDialogType,
    options?: { extensionData?: ExtensionProspectData | ExtensionOrganizationData; editMode?: boolean; id?: number }
  ) => void
  openSubDialog: (subType: FormDialogType) => void
  closeDialog: () => void
  closeSubDialog: () => void
  state: State
}

/* Context */
const FormContext = createContext<FormContextProps>({
  openDialog: _noop,
  closeDialog: _noop,
  openSubDialog: _noop,
  closeSubDialog: _noop,
  state: {} as State,
})

FormContext.displayName = 'FormContext'

/* Provider */
function FormProvider(props: { children: ReactElement }): ReactElement {
  const { children } = props
  const theme = useTheme()
  const { t } = useTranslation()
  const [state, setState] = useState<State>({ open: false, openSubDialog: false })
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
  const FormDialog = _get(FormType, state.type as FormDialogType, Fragment)
  const SubFormDialog = _get(FormType, state.subType as FormDialogType, Fragment)
  const openDialog = (
    type: FormDialogType,
    options?: { extensionData?: ExtensionProspectData | ExtensionOrganizationData; editMode?: boolean; id?: number }
  ) => setState((prev) => ({ ...prev, open: true, type, options }))
  const openSubDialog = (subType: FormDialogType) => setState((prev) => ({ ...prev, openSubDialog: true, subType }))
  const closeDialog = () => setState((prev) => ({ ...prev, open: false }))
  const closeSubDialog = () => setState((prev) => ({ ...prev, openSubDialog: false }))
  const FormContextProps: FormContextProps = { openDialog, closeDialog, openSubDialog, closeSubDialog, state }

  useGetExtensionMessage({ openDialog, closeDialog })

  const items = [
    {
      title: t('newTask'),
      id: FormDialogType.task,
      icon: <FormatListBulletedIcon color="primary" />,
    },
    {
      title: t('newDeal'),
      id: FormDialogType.deal,
      icon: <AttachMoneyIcon color="primary" />,
    },
    {
      title: t('newOrganization'),
      id: FormDialogType.organization,
      icon: <BusinessOutlinedIcon color="primary" />,
    },
    {
      title: t('newProspect'),
      id: FormDialogType.prospect,
      icon: <PersonIcon color="primary" />,
    },
  ]

  return (
    <FormContext.Provider value={FormContextProps}>
      {children}

      <StyledGrid
        container
        item
        gap={2}
        direction="column"
        sx={{ position: 'absolute', zIndex: '99', bottom: '50px', right: '20px', width: 'fit-content' }}
      >
        <Grid item direction="column" container spacing={2} className={classes.group}>
          {items.map((item) => (
            <Grid item key={item.id}>
              <Tooltip title={item.title} placement="left" sx={{ top: '0' }}>
                <Fab
                  size="medium"
                  onClick={() => openDialog(item.id)}
                  sx={{ bgcolor: 'common.white', border: '1px solid #00000033' }}
                >
                  {item.icon}
                </Fab>
              </Tooltip>
            </Grid>
          ))}
        </Grid>

        <Grid item className={classes.shortCutBtn}>
          <Fab
            size="medium"
            sx={{
              bgcolor: 'common.white',
              border: '1px solid',
              borderColor: 'primary.main',
              '&:hover': {
                boxShadow: 'none',
              },
            }}
          >
            <img alt="leadliaison" src="/favicon.ico" style={{ width: 30 }} />
          </Fab>
        </Grid>
      </StyledGrid>

      <Suspense fallback={<Spinner size={24} />}>
        {!state.openSubDialog ? (
          <Dialog fullWidth open={state.open} onClose={closeDialog} fullScreen={fullScreen}>
            <FormDialog />
          </Dialog>
        ) : null}
      </Suspense>

      <Suspense fallback={<Spinner size={24} />}>
        {state.openSubDialog ? (
          <Dialog open={state.openSubDialog} onClose={closeSubDialog} fullWidth fullScreen={fullScreen}>
            {SubFormDialog ? <SubFormDialog subDialog /> : null}
          </Dialog>
        ) : null}
      </Suspense>
    </FormContext.Provider>
  )
}

/* useContext */
function useFormDialog(): FormContextProps {
  const context = useContext(FormContext)

  if (context === undefined) {
    throw new Error(`FormContext must be used within a FormProvider`)
  }

  return useMemo(() => context || {}, [context])
}

export { FormProvider, useFormDialog }
